import { pki as ForgePki, util as ForgeUtil } from 'node-forge';
import { isNil } from '@lucidlink/tscore';
export const createRsaCrypto = ({ scheme, serializedPublicKey, serializedPrivateKey }) => {
    return new NodeForgeRsaCrypto(scheme, serializedPublicKey, serializedPrivateKey);
};
export const RSA_DEFAULT_SCHEME = 'RSA-OAEP';
export const RSA_KEY_LENGTH_BITS = 2048;
export const RSA_ENCRYPTION_OAEP_MAX_BYTES_DECRYPTED = 214;
class NodeForgeRsaCrypto {
    scheme;
    publicKey;
    privateKey;
    constructor(scheme, serializedPublicKey, serializedPrivateKey) {
        this.scheme = scheme;
        if (scheme != RSA_DEFAULT_SCHEME) {
            throw new Error('Only RSA-OAEP is currently supported!');
        }
        if (serializedPublicKey) {
            this.publicKey = ForgePki.publicKeyFromPem(serializedPublicKey);
        }
        if (serializedPrivateKey) {
            this.privateKey = ForgePki.privateKeyFromPem(serializedPrivateKey);
        }
        if (isNil(serializedPublicKey) && isNil(serializedPrivateKey)) {
            const pair = ForgePki.rsa.generateKeyPair(RSA_KEY_LENGTH_BITS);
            this.publicKey = pair.publicKey;
            this.privateKey = pair.privateKey;
        }
    }
    serializePrivateKey() {
        return ForgePki.privateKeyToPem(this.privateKey);
    }
    serializePublicKey() {
        return ForgePki.publicKeyToPem(this.publicKey);
    }
    encrypt(unencrypted) {
        const encrypted = this.publicKey.encrypt(this.bufferToBinaryString(unencrypted), this.scheme);
        return this.binaryStringToBuffer(encrypted);
    }
    decrypt(encrypted) {
        const decrypted = this.privateKey.decrypt(this.bufferToBinaryString(encrypted), this.scheme);
        return this.binaryStringToBuffer(decrypted);
    }
    encryptLong(unencrypted) {
        const res = new Array();
        let pos = 0;
        while (pos < unencrypted.length) {
            const segmentLength = Math.min(RSA_ENCRYPTION_OAEP_MAX_BYTES_DECRYPTED, unencrypted.length - pos);
            const toEncrypt = unencrypted.slice(pos, pos + segmentLength);
            const encrypted = this.encrypt(toEncrypt);
            res.push(encrypted);
            pos += segmentLength;
        }
        return res;
    }
    decryptLong(encrypted) {
        let res = new Uint8Array();
        for (const encryptedEl of encrypted) {
            res = new Uint8Array([...res, ...this.decrypt(encryptedEl)]);
        }
        return res;
    }
    generateRsaPair() {
        const pair = ForgePki.rsa.generateKeyPair(RSA_KEY_LENGTH_BITS);
        return {
            publicKey: ForgePki.publicKeyToPem(pair.publicKey),
            privateKey: ForgePki.privateKeyToPem(pair.privateKey),
        };
    }
    bufferToBinaryString(bytes) {
        return new ForgeUtil.ByteStringBuffer(bytes).getBytes();
    }
    binaryStringToBuffer(str) {
        return ForgeUtil.binary.raw.decode(str);
    }
}
